// Order additions — shared pure helpers (order-additions spec, Component 7).
//
// Mirrors admin-mobile-app/src/utils/addableOrder.js. Keep the two byte-identical
// apart from the one header line naming the mirror direction, following the existing
// money.js / menuName.js duplication convention.
//
// THESE MUST AGREE WITH THE BACKEND. `is_addable_order` / `is_bill_stale` in
// lambda/common/pos_order_core.py are the authority — the server independently rejects
// an ineligible order with 409 — but a UI that disagrees would either offer an action
// that always fails or hide one that would have worked. One rule, expressed twice.
//
// Deliberately NOT imported from AdminPosQueue.js. That file is >5,000 lines with its
// own concerns; Take Order must never depend on it. Both screens import this instead.

// Payment states that close an order to further additions.
const SETTLED_PAYMENT_STATES = ['paid', 'refunded'];

// Order states that close an order to further additions.
const CLOSED_ORDER_STATES = ['CANCELLED', 'CANCELED', 'COMPLETED'];

// Fulfillment states that close an order to further additions.
const CLOSED_FULFILLMENT_STATES = [
  'CANCELLED', 'CANCELED', 'CANCEL',
  'PICKUP_COMPLETE', 'PICKUP COMPLETE', 'PICKED_UP', 'PICKED UP',
  'DELIVERED', 'DELIVERY_COMPLETE', 'DELIVERY COMPLETE',
  'DINE_IN_COMPLETE', 'DINE-IN COMPLETE', 'DINE IN COMPLETE', 'DINEIN_COMPLETE',
  'COMPLETED',
];

// Every alias the backend inspects, checked INDEPENDENTLY rather than with `||`.
const PAYMENT_KEYS = ['payment_status', 'paymentStatus'];
const STATUS_KEYS = ['status', 'order_status'];
const FULFILLMENT_KEYS = ['adminFulfillmentStatus', 'posFulfillmentState', 'squareFulfillmentState'];

function lower(v) {
  return String(v == null ? '' : v).trim().toLowerCase();
}

function upper(v) {
  return String(v == null ? '' : v).trim().toUpperCase();
}

/**
 * isAddableOrder(order) -> boolean
 *
 * True only for a STAFF order that is unpaid, un-refunded, not cancelled and not
 * completed. Deliberately conservative: anything unrecognized reads as NOT addable,
 * because wrongly offering to append to a settled or third-party order is far worse
 * than wrongly hiding the action.
 *
 * The `orderSource === 'staff'` condition is the important one. Appending to a
 * marketplace order (Otter / UberEats / DoorDash) or a customer order would
 * desynchronize a third party that has no idea the order changed.
 *
 * WHY EVERY ALIAS IS CHECKED SEPARATELY: an order record carries BOTH
 * `payment_status` ("pending_payment") and `paymentStatus` ("PENDING_PAYMENT"), so
 * `a || b` short-circuits on the first truthy one and never reads the second — which
 * would treat a record whose `paymentStatus` said PAID as still addable. The backend
 * had exactly that bug before a test caught it. If ANY alias says settled, it is
 * settled.
 */
export function isAddableOrder(order) {
  if (!order || typeof order !== 'object') return false;

  if (lower(order.orderSource) !== 'staff') return false;

  if (PAYMENT_KEYS.some((k) => SETTLED_PAYMENT_STATES.includes(lower(order[k])))) return false;
  if (STATUS_KEYS.some((k) => CLOSED_ORDER_STATES.includes(upper(order[k])))) return false;
  if (FULFILLMENT_KEYS.some((k) => CLOSED_FULFILLMENT_STATES.includes(upper(order[k])))) {
    return false;
  }

  return true;
}

/** Epoch seconds when a guest bill was last printed, else null. */
export function billPrintedAt(order) {
  const n = Number((order || {}).billPrintedAt);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
}

/** Epoch seconds of the most recent amendment, else null. */
export function lastAmendmentAt(order) {
  const log = (order || {}).amendments;
  if (!Array.isArray(log) || log.length === 0) return null;
  const stamps = log
    .map((e) => (e && typeof e === 'object' ? Number(e.at) : NaN))
    .filter((n) => Number.isFinite(n) && n > 0);
  return stamps.length ? Math.max(...stamps) : null;
}

/**
 * isBillStale(order) -> boolean
 *
 * Whether a printed guest bill no longer matches the order.
 *
 * DERIVED, never read from a stored boolean. A stored flag would be a second source of
 * truth able to disagree with the amendment log, and the log is the auditable one.
 *
 * A bill printed in the same second as the amendment counts as FRESH (strict `<`), on
 * the assumption it included it. The backend uses the same boundary.
 */
export function isBillStale(order) {
  const printed = billPrintedAt(order);
  if (printed === null) return false;
  const amended = lastAmendmentAt(order);
  if (amended === null) return false;
  return printed < amended;
}

/** How many times an order has been added to. 0 when never. */
export function amendmentCount(order) {
  const log = (order || {}).amendments;
  return Array.isArray(log) ? log.length : 0;
}

/**
 * Resolve a field that may live on the order or be mirrored into
 * `customerSubmittedSnapshot`. The queue and both print builders read from the
 * snapshot, so a field can legitimately appear in either place.
 */
export function orderField(order, key) {
  if (!order || typeof order !== 'object') return undefined;
  if (order[key] != null) return order[key];
  const snap = order.customerSubmittedSnapshot;
  if (snap && typeof snap === 'object' && snap[key] != null) return snap[key];
  return undefined;
}

/**
 * Short human label identifying a ticket: table plus party when present.
 * Returns '' when there is nothing to say, so callers can render nothing.
 */
export function ticketLabel(order) {
  const table = String(orderField(order, 'tableLabel') || '').trim();
  const party = String(orderField(order, 'partyLabel') || '').trim();
  if (!table) return party ? `Party ${party}` : '';
  return party ? `Table ${table} · ${party}` : `Table ${table}`;
}

/**
 * Total quantity across an order's lines — what the strip shows as "N items".
 * Reads `items` then the snapshot mirror, and tolerates either quantity casing.
 */
export function orderItemCount(order) {
  let items = (order || {}).items;
  if (!Array.isArray(items)) {
    const snap = (order || {}).customerSubmittedSnapshot;
    items = snap && Array.isArray(snap.items) ? snap.items : [];
  }
  return items.reduce((acc, it) => {
    if (!it || typeof it !== 'object') return acc;
    const q = Number(it.Quantity != null ? it.Quantity : it.quantity);
    return acc + (Number.isFinite(q) && q > 0 ? Math.trunc(q) : 0);
  }, 0);
}
